import { UserInfoModel } from "app/model/ua/user-info";
import { UserMenuModel } from "app/model/ua/user-menu";
import { AuthConstant } from "app/constants/authConstant";

/**
 * 디자인 미리보기 모드.
 *
 * ── 왜 필요한가
 *
 * 화면 디자인을 손볼 때마다 로그인해야 하면, 인증 서버가 멈춰 있거나 계정이 없을 때
 * 화면을 아예 볼 수 없다. 이 모드는 로그인 없이 레이아웃과 화면 뼈대를 띄운다.
 *
 * ── 무엇을 바꾸는가
 *
 *  - 로그인 판단을 '로그인됨'으로 고정하고 가짜 사용자를 넣는다.
 *  - 메뉴를 API 대신 아래 고정 메뉴(Figma 메뉴구조도 기준)로 채운다.
 *  - 401 이 와도 로그인으로 보내지 않는다. 데이터는 비어 보인다.
 *
 * ── 어떻게 켜는가
 *
 *  `.env.development.local`(git 이 무시한다)에 `REACT_APP_DESIGN_PREVIEW=true` 를 넣고
 *  개발 서버를 다시 띄운다. 역할은 `?role=admin` / `?role=user` 로 바꾼다(주소의 # 앞).
 *
 * ★ 개발 빌드에서만 켜진다. 운영 빌드에서는 NODE_ENV 가 production 이라 항상 꺼진다.
 *   작은 보조 함수는 번들에 남지만 실행되지 않고, 고정 메뉴는 호출 시점에 만들어 번들에서 빠진다.
 */
export const DESIGN_PREVIEW: boolean =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_DESIGN_PREVIEW === "true";

export type PreviewRole = "admin" | "user";

const ROLE_STORAGE_KEY = "designPreviewRole";

export function getPreviewRole(): PreviewRole {
  const fromQuery = new URLSearchParams(window.location.search).get("role");
  if (fromQuery === "admin" || fromQuery === "user") {
    window.localStorage.setItem(ROLE_STORAGE_KEY, fromQuery);
    return fromQuery;
  }
  return window.localStorage.getItem(ROLE_STORAGE_KEY) === "admin" ? "admin" : "user";
}

export const PREVIEW_USER_ID = "design-preview";

export function previewUserInfo(): UserInfoModel {
  const authNm = getPreviewRole() === "admin" ? AuthConstant.ADMIN : AuthConstant.R001;
  return new UserInfoModel({
    userId: PREVIEW_USER_ID,
    userNm: "디자인 미리보기",
    userEmailAddr: "preview@eneruse.local",
    aprYn: "Y",
    useYn: "Y",
    auths: [{ authId: authNm, authNm }],
  });
}

let seq = 0;
function menu(menuNm: string, menuUrlAddr: string, subMenus: UserMenuModel[] = []): UserMenuModel {
  seq += 1;
  const menuId = `PV${String(seq).padStart(3, "0")}`;
  return {
    menuId,
    menuNm,
    menuUrlAddr,
    menuMngSeqno: seq,
    subMenus: subMenus.map((sub) => ({ ...sub, uppoMenuId: menuId })),
  };
}

/** Figma '메뉴구조도_기업' 기준. 운영 번들에서 빠지도록 모듈 로드 시점이 아니라 호출 시점에 만든다. */
const userMenus = (): UserMenuModel[] => [
  menu("에너지 현황", "/pc", [
    menu("전기사용량", "/pc/eu/supplyconsumption"),
    menu("발전현황", "/pc/renewal/solar"),
  ]),
  menu("RE100", "/rp", [
    menu("목표관리", "/rp/rdmpGoalSetting"),
    menu("RE100 포트폴리오", "/rp/portfolio"),
    menu("실적현황", "/rp/rdmp"),
    menu("인증 관리", "/rp/smcnCert", [
      menu("이행수단 실적 등록", "/rp/smcnCert"),
      menu("재생에너지 사용 확인서 관리", "/rp/useConfirmationList"),
    ]),
    menu("자료실", "/rp/rlibr"),
    menu("RE100 시세 조회", "/rp/impleWayAnalysis"),
  ]),
  menu("장외거래", "/ot", [
    menu("REC 장외시장", "/ot/rec/trade"),
    menu("거래현황", "/ot/rec/contract"),
    menu("계약관리", "/ot/contract"),
    menu("부정거래 신고", "/ot/trade/rpdcs"),
  ]),
  menu("보고서", "/report/reportUser"),
  menu("정보공개", "/info", [
    menu("공지사항", "/info/info001"),
    menu("FAQ", "/info/info002"),
    menu("Q&A", "/info/info003"),
    menu("Open API", "/info/api/list", [
      menu("Open API 조회", "/info/api/list"),
      menu("API 활용신청 현황", "/info/api/status"),
    ]),
  ]),
];

/** Figma '메뉴구조도_관리자' 기준. */
const adminMenus = (): UserMenuModel[] => [
  menu("모니터링", "/pdcs", [
    menu("전기 사용량", "/pdcs/splycs"),
    menu("신재생 현황", "/pdcs/pwrstationstatus"),
    menu("RE100 실적", "/ot/re100/status", [
      menu("RE100 현황", "/ot/re100/status"),
      menu("이행수단별 실적", "/ot/re100/fufl"),
    ]),
  ]),
  menu("장외거래", "/ot", [
    menu("장외거래 실적(REC)", "/ot/oct/status"),
    menu("부정거래 신고", "/ot/trade/rpdcs"),
  ]),
  menu("보고서", "/report/reportAdmin"),
  menu("정보공개", "/info", [
    menu("공지사항", "/info/info001"),
    menu("FAQ", "/info/info002"),
    menu("Q&A", "/info/info003"),
    menu("Open API", "/info/api/list"),
  ]),
  menu("시스템관리", "/sys", [
    menu("사용자 관리", "/sys/userMng"),
    menu("메뉴/프로그램 관리", "/sys/sysMenu", [
      menu("메뉴 관리", "/sys/sysMenu"),
      menu("프로그램 관리", "/sys/sysPgm"),
    ]),
    menu("자료실 관리", "/rp/rlibr"),
    menu("시스템 정보 관리", "/sys/info/sysAcs", [
      menu("시스템 접속 정보", "/sys/info/sysAcs"),
      menu("시스템 사용 통계", "/sys/info/sysStat"),
    ]),
    menu("알람 관리", "/sys/sysAlarm"),
  ]),
];

export function previewMenus(): UserMenuModel[] {
  seq = 0;
  return getPreviewRole() === "admin" ? adminMenus() : userMenus();
}
